import React, { useState, useEffect } from 'react';
import { message, Spin } from 'antd';
import client from 'helpers/client';
import cognito from 'helpers/cognito';
import GeneralTab from './codebase/GeneralTab';
import ReleasesTab from './codebase/ReleasesTab';
import NFTLicensesTab from './codebase/NFTLicensesTab';
import RevenueShareTab from './codebase/RevenueShareTab';
import IssuedLicensesTab from './codebase/IssuedLicensesTab';

const ALL_TABS = [
  { key: 'RELEASES', label: 'Releases' },
  { key: 'NFT_LICENSES', label: 'NFT Licenses' },
  { key: 'ISSUED_LICENSES', label: 'Issued Licenses' },
  { key: 'REVENUE_SHARE', label: 'Revenue Share' },
];

const CREATE_TABS = [
  { key: 'GENERAL', label: 'General' },
];



const CodebaseDetailModal = ({ 
  open, 
  onClose, 
  codebase, 
  isCreateMode, 
  daoId,
  chainId,
  daoInfo,
  onSuccess
}) => {
  const [editedCodebase, setEditedCodebase] = useState(codebase);
  const [selectedTab, setSelectedTab] = useState('RELEASES');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (isCreateMode) {
        setEditedCodebase({
          name: '',
          description: '',
          repository: '',
          status: 'active',
          tags: '',
          license: '',
          daoId
        });
      } else if (codebase) {
        setEditedCodebase(codebase);
      }
      setSelectedTab('RELEASES');
    }
  }, [open, isCreateMode, codebase, daoId]);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        daoId
      };

      let response;
      if (isCreateMode) {
        response = await client.api('/codebases', 'post', payload, {
          headers: await cognito.getHeaderAuthorization()
        });
        message.success('Codebase created successfully!');
      } else {
        response = await client.api(`/codebases/${codebase._id}`, 'put', payload, {
          headers: await cognito.getHeaderAuthorization()
        });
        message.success('Codebase updated successfully!');
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving codebase:', error);
      message.error(error.response?.data?.message || 'Failed to save codebase');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'GENERAL':
        return (
          <GeneralTab
            editedCodebase={editedCodebase}
            onSave={handleSave}
            onCancel={handleCancel}
            isCreateMode={isCreateMode}
            daoId={daoId}
            chainId={chainId}
          />
        );
      case 'RELEASES':
        return <ReleasesTab codebase={editedCodebase} chainId={chainId} />;
      case 'NFT_LICENSES':
        return <NFTLicensesTab codebase={editedCodebase} chainId={chainId} daoInfo={daoInfo} />;
      case 'ISSUED_LICENSES':
        return <IssuedLicensesTab codebase={editedCodebase} chainId={chainId} daoInfo={daoInfo} />;
      case 'REVENUE_SHARE':
        return <RevenueShareTab codeBaseDetail={editedCodebase} chainId={chainId} daoInfo={daoInfo} />;
      default:
        return null;
    }
  };

  // Choose tab list based on create or edit mode
  const tabs = isCreateMode ? CREATE_TABS : ALL_TABS;

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh',
      background: 'rgba(0,0,0,0.4)', 
      zIndex: 1000, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff', 
        borderRadius: 4, 
        width: '90vw', 
        height: '90vh', 
        maxWidth: '90vw', 
        maxHeight: '90vh', 
        overflowY: 'auto', 
        padding: 0, 
        position: 'relative', 
        display: 'flex'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute', 
            top: 16, 
            right: 16, 
            background: 'transparent', 
            border: 'none', 
            fontSize: 24, 
            cursor: 'pointer', 
            zIndex: 2
          }}
        >×</button>

        {/* Sidebar Tabs */}
        <div style={{ 
          width: 220, 
          backgroundColor: '#282f39',
          borderTopLeftRadius: '8px',
          borderBottomLeftRadius: '8px'
        }}>
          <ul style={{
            listStyleType: 'none',
            paddingLeft: 0,
            margin: 0
          }}>
            {tabs.map(tab => {
              const isActive = selectedTab === tab.key;
              return (
                <li key={tab.key} style={{ lineHeight: '54px' }}>
                  <a 
                    onClick={() => setSelectedTab(tab.key)}
                    style={{ 
                      cursor: 'pointer',
                      color: isActive ? '#1AEEC2' : '#8CA0A7',
                      textDecoration: 'none',
                      display: 'block',
                      padding: '0 24px',
                      transition: 'color 0.2s'
                    }}
                    onMouseOver={(e) => {
                      if (!isActive) e.target.style.color = '#1AEEC2';
                    }}
                    onMouseOut={(e) => {
                      if (!isActive) e.target.style.color = '#8CA0A7';
                    }}
                  >
                    {tab.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, padding: 32, minWidth: 0 }}>
          <Spin spinning={loading}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2>
                <span> {isCreateMode ? 'New Codebase' : ('Codebase: ' + (editedCodebase?.name || ''))}</span>
              </h2>
            </div>
            <div style={{ width: '100%', height: 'calc(90vh - 120px)', overflowY: 'auto' }}>
              {renderTabContent()}
            </div>
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default CodebaseDetailModal; 